export enum AccountState {
  // See: https://taktak.atlassian.net/wiki/spaces/ENG/pages/1779499247/Account+state+machine+and+pools
  /** ********** ROOT OU *********** */
  AccountProv = 'Account-Prov',
  /** ********** Account Provisioning OU *********** */
  AccountReady = 'Account-Ready',
  /** ********** Maestro Provisioning OU *********** */
  TerraformProv = 'Terraform-Prov',
  PreProv = 'Pre-Prov',
  /** Pre-prov triggered via API */
  TerraformPreProvSuccess = 'Terraform-Pre-Prov-Success',
  TerraformPreProvFailed = 'Terraform-Pre-Prov-Failed',
  /**
   * @deprecated shouldn't be used anymore, will be deleted soon
   */
  PostProv = 'Post-Prov',
  /** ********** Assignable OU *********** */
  TenantReady = 'Tenant-Ready',
  /** ********** Assigned OU *********** */
  TenantAssigned = 'Tenant-Assigned',
  TenantUnassigned = 'Tenant-Unassigned',
  // ...More to be added when applicable
  /** ********** Cleanup OU *********** */
  CleanupRequested = 'Cleanup-Requested',
  CleanupStarted = 'Cleanup-Started',
  CleanupCompleted = 'Cleanup-Completed',
  CleanupFailed = 'Cleanup-Failed',
  /** ********** Available OU *********** */
  AccountAvailable = 'Account-Available',
  /** ********** Failed OU *********** */
  TenantFailed = 'Tenant-Failed',
  // Persisted state set by OrganizationService.handleFatalFailure when a fatal DLQ event is handled:
  // the org is transitioned here and managed cleanup is requested. Member of cleanableStates.
  TenantFailedDLQ = 'Tenant-FailedDLQ',
  TenantBanished = 'Tenant-Banished', // organization record surgically updated due to inconsistent state during migration from AWS tags
  /** ********** Workspaces *********** */
  WorkspaceRequested = 'Workspace-Requested',
  WorkspaceProvisioning = 'Workspace-Provisioning',
  WorkspaceProvisioningFailed = 'Workspace-Provisioning-Failed',
  WorkspaceProvisioned = 'Workspace-Provisioned',
  WorkspaceActive = 'Workspace-Active',
  WorkspaceAsleep = 'Workspace-Asleep',
  WorkspaceAsleepRequested = 'Workspace-Asleep-Requested',
  WorkspaceWakeRequested = 'Workspace-Wake-Requested',
  WorkspaceUpdated = 'Workspace-Updated',
  WorkspaceFailedUpdate = 'Workspace-Failed-Update',
  WorkspaceCleanupRequested = 'Workspace-Cleanup-Requested',
  WorkspaceCleanupStarted = 'Workspace-Cleanup-Started',
  WorkspaceCleanupTerraformCompleted = 'Workspace-Cleanup-Terraform-Completed',
  WorkspaceCleanupTerraformFailed = 'Workspace-Cleanup-Terraform-Failed',
  WorkspaceCleanupCompleted = 'Workspace-Cleanup-Completed',
  WorkspaceCleanupFailed = 'Workspace-Cleanup-Failed',
}

export type WorkspaceState =
  | AccountState.WorkspaceRequested |
  AccountState.WorkspaceProvisioning |
  AccountState.WorkspaceProvisioningFailed |
  AccountState.WorkspaceProvisioned |
  AccountState.WorkspaceActive |
  AccountState.WorkspaceAsleep |
  AccountState.WorkspaceAsleepRequested |
  AccountState.WorkspaceWakeRequested |
  AccountState.WorkspaceCleanupRequested |
  AccountState.WorkspaceCleanupStarted |
  AccountState.WorkspaceCleanupTerraformCompleted |
  AccountState.WorkspaceCleanupTerraformFailed |
  AccountState.WorkspaceCleanupCompleted |
  AccountState.WorkspaceCleanupFailed;

export const AllAccountStates = Object.values(AccountState);

const accountStateValues = new Set<string>(AllAccountStates);

export const isAccountState = (value: unknown): value is AccountState =>
  typeof value === 'string' && accountStateValues.has(value);
export const WORKSPACE_STATE_PREFIX = 'Workspace-';

export const AllWorkspaceStates = AllAccountStates.filter((i) => i.startsWith(WORKSPACE_STATE_PREFIX));
export const AllActiveWorkspaceStates = AllWorkspaceStates.filter((i) => i.indexOf('Cleanup') < 0);

export const AllPersistedWorkspaceStates: readonly WorkspaceState[] = AllWorkspaceStates.filter(
  (state): state is WorkspaceState =>
    state !== AccountState.WorkspaceUpdated && state !== AccountState.WorkspaceFailedUpdate,
);

/**
 * Organization states that can be written on an organization record. Excludes workspace states
 * (those live on the workspaces table) and Terraform pre-prov success/failed, which are event
 * names only and are never assigned to an organization.
 */
export const AllPersistedOrganizationStates: readonly AccountState[] = AllAccountStates.filter(
  (state) =>
    !state.startsWith(WORKSPACE_STATE_PREFIX) &&
    state !== AccountState.TerraformPreProvSuccess &&
    state !== AccountState.TerraformPreProvFailed,
);

/**
 * True when an event name identifies a workspace-scoped event.
 *
 * Deliberately keyed on the event name rather than on the presence of a `workspaceId`: several
 * organization-scoped events (`Tenant-Ready`, `Tenant-Failed`) are published *with* a `workspaceId`
 * to carry the workspace that triggered them, so `workspaceId` alone misclassifies them.
 *
 * Matches on the `Workspace-` prefix rather than on membership of `AllWorkspaceStates` so the check
 * fails *closed*: an event name this build does not know about (a newer publisher during a deploy
 * skew, or a state added to the enum later) is still treated as workspace-scoped rather than
 * escalating to the organization. `AllWorkspaceStates` is filtered on the same prefix constant, so
 * the two agree for every state defined here.
 *
 * Returns `boolean` rather than narrowing to `WorkspaceState` on purpose — that union omits
 * `Workspace-Updated` and `Workspace-Failed-Update`, which are real workspace events and do match
 * here, so narrowing would be unsound.
 */
export const isWorkspaceScopedEventName = (value: unknown): boolean =>
  typeof value === 'string' && value.startsWith(WORKSPACE_STATE_PREFIX);

// Terraform-Prov and Account-Ready are intentionally absent (PLAT-16109). Both are early,
// in-flight provisioning states, not garbage: on 2026-09-16, 12 organizations parked in early
// states -- Terraform-Prov among them -- were recovered rather than stuck. 11 reached
// Tenant-Ready in 3-9 minutes once the right event was resent, and one was claimed and
// released within minutes of arriving. A reap-first cleanup path would have destroyed that
// recoverable inventory. The intended recovery route for a stuck Terraform-Prov organization
// is the forced transition (Admin API patchOrganizationHandler -> transitionState), not
// organizations:cleanup.
//
// Coverage that keeps this list honest lives in OrganizationService.test.ts, describe(
// 'handleFatalFailure cleanup eligibility'): the it.each reason assertion and the completeness
// test. Adding a state here without updating both tables fails the completeness test by design.
export const cleanableStates: AccountState[] = [
  AccountState.AccountProv, // Failure to launch
  AccountState.PreProv,
  AccountState.PostProv,
  // TODO This state should be removed and fix testing accordingly
  // We don't want this state be cleanable
  AccountState.TenantReady, // Assignable
  AccountState.TenantUnassigned, // Not assigned anymore
  AccountState.CleanupFailed,
  AccountState.AccountAvailable,
  AccountState.CleanupRequested,
  AccountState.TenantFailed,
  AccountState.TenantFailedDLQ,
];

export const restorableStates: AccountState[] = [
  AccountState.TenantUnassigned, // Not assigned anymore
  AccountState.CleanupFailed,
  AccountState.CleanupCompleted,
];

/**
 * The organization-scoped cleanup transitions -- the states that tear down the whole org rather than
 * a single workspace. Distinct from `cleanableStates`, which answers "may cleanup be *started* from
 * this state": none of these three is in that list, and a legitimate Cleanup-Completed arrives while
 * the org sits in Cleanup-Started.
 *
 * Used by the guards that refuse to drive a Tenant-Assigned (live) org through cleanup -- one in
 * `OrganizationService.transitionState`, one in `TenantMessenger.handleCleanupMessage`. Kept here so
 * a fourth org-scoped cleanup state cannot be added to one guard and missed by the other.
 * Workspace-scoped teardown events are deliberately absent: they legitimately arrive for a live org.
 */
export const orgScopedCleanupStates: readonly AccountState[] = [
  AccountState.CleanupStarted,
  AccountState.CleanupCompleted,
  AccountState.CleanupFailed,
];

export const workspaceFailedStates: readonly AccountState[] = [
  AccountState.WorkspaceProvisioningFailed,
  AccountState.WorkspaceCleanupTerraformFailed,
  AccountState.WorkspaceCleanupFailed,
];

// Narrower than workspaceFailedStates: only states reached AFTER a cleanup attempt was made and failed.
// WorkspaceProvisioningFailed is deliberately excluded -- a workspace that never got a cleanup attempt
// is still cleanable and must not count toward the managed-cleanup all-failed tripwire (INFRA-11349).
export const workspaceTerminalCleanupFailedStates: readonly AccountState[] = [
  AccountState.WorkspaceCleanupTerraformFailed,
  AccountState.WorkspaceCleanupFailed,
];

export const workspaceDeletingStates: readonly AccountState[] = [
  AccountState.WorkspaceCleanupRequested,
  AccountState.WorkspaceCleanupStarted,
  AccountState.WorkspaceCleanupTerraformCompleted,
  AccountState.WorkspaceCleanupCompleted,
];

export const workspaceCleanupStates: readonly AccountState[] = [
  ...workspaceDeletingStates,
  ...workspaceTerminalCleanupFailedStates,
];

export const workspaceHibernationStates: readonly AccountState[] = [
  AccountState.WorkspaceAsleep,
  AccountState.WorkspaceAsleepRequested,
  AccountState.WorkspaceWakeRequested,
];

// States that we restrict the UI to be able to delete in: active/failed
// This prevents delete requests causing terraform collisions when transitioning states
// EXCLUDE WorkspaceRequested to prevent cleanup during initial provisioning setup
export const workspaceDeletableStates = [
  ...AllActiveWorkspaceStates.filter((state) => state !== AccountState.WorkspaceRequested),
  ...workspaceFailedStates,
];
